import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronRight, Inbox } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation, Trans } from 'react-i18next';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { apiFetch, ApiError } from '@/lib/api';
import { licenseDetailSchema, licenseAuditSchema } from '@/schemas/licenses';
import { robotPassportSchema } from '@/schemas/passport';
import { classifySerial } from '@/lib/serial';
import { licenseDevicesSchema, deviceActivationItemSchema } from '@/schemas/dashboard';
import { activationRequestListSchema } from '@/schemas/activation-requests';
import type { LicenseDetail, AuditEvent } from '@/schemas/licenses';
import type { ActivationRequest } from '@/schemas/activation-requests';
import type { z } from 'zod';
import { LicenseDetailHeader } from '@/components/app/license-detail-header';
import { LicenseMetadataGrid } from '@/components/app/license-metadata-grid';
import { LicenseActivationsTable } from '@/components/app/license-activations-table';
import { LicenseActivityTimeline } from '@/components/app/license-activity-timeline';
import { RobotPassport } from '@/components/app/robot-passport';
import { PassportHistory } from '@/components/app/passport-history';
import ConfirmDestructiveDialog from '@/components/app/confirm-destructive-dialog';

/// A live seat of a license — a transfer candidate in the auto-activation flow.
type LiveSeat = {
  id: string;
  installationId: string | null;
  fingerprintHash: string;
  activatedAt: string;
};

async function fetchLicense(id: string): Promise<LicenseDetail> {
  const resp = await apiFetch(`/licenses/${id}`);
  const json = await resp.json();
  return licenseDetailSchema.parse(json);
}

async function fetchDevices(id: string) {
  const resp = await apiFetch(`/licenses/${id}/devices`);
  const json = await resp.json();
  return licenseDevicesSchema.parse(json);
}

async function fetchAuditFallback(id: string) {
  const resp = await apiFetch(`/licenses/${id}/devices`);
  const json = await resp.json();
  const parsed = licenseDevicesSchema.parse(json);
  const events: AuditEvent[] = parsed.data.map(
    (act: z.infer<typeof deviceActivationItemSchema>, i: number) => ({
      id: `dev-${i}`,
      timestamp: act.activatedAt,
      event: 'activation',
      serial: '',
      licenseId: act.licenseId,
      performedBy: '(system)',
      ipAddress: null,
      metadata: { fingerprint: act.fingerprintHash },
    })
  );
  return { data: events, total: parsed.data.length, page: 1, pages: 1 };
}

async function fetchAudit(serial: string, page: number) {
  const resp = await apiFetch(
    `/licenses/audit?serial=${encodeURIComponent(serial)}&page=${page}&limit=10`
  );
  const json = await resp.json();
  return licenseAuditSchema.parse(json);
}

async function fetchPassport(serial: string, page: number) {
  const resp = await apiFetch(
    `/licenses/serial/${encodeURIComponent(serial)}/passport?page=${page}&limit=10`
  );
  const json = await resp.json();
  return robotPassportSchema.parse(json);
}

async function revokeLicense(id: string, reason?: string): Promise<void> {
  await apiFetch(`/licenses/${id}/revoke`, {
    method: 'POST',
    body: JSON.stringify({ reason: reason ?? 'Manual revocation from dashboard' }),
  });
}

async function fetchActivationRequests(id: string) {
  const resp = await apiFetch(`/licenses/${id}/activation-requests`);
  const json = await resp.json();
  return activationRequestListSchema.parse(json);
}

async function approveActivationRequest(requestId: string, seat: string): Promise<void> {
  // The operator's explicit choice: the live seat this transfer frees. The server
  // rejects a missing seat (`Seat is required`), so it is always sent.
  await apiFetch(`/licenses/activation-request/${requestId}/approve`, {
    method: 'POST',
    body: JSON.stringify({ seat }),
  });
}

async function rejectActivationRequest(requestId: string, reason: string): Promise<void> {
  await apiFetch(`/licenses/activation-request/${requestId}/reject`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

async function offlineDownload(
  id: string,
  installationId: string | null
): Promise<{ licenseFile: string | object }> {
  // V2 (RRL2 envelope) requires installationId — use it when the license has a
  // live seat; otherwise fall back to the legacy v1 path (BA legacy).
  const body = installationId
    ? { performedBy: 'dashboard', version: 2, installationId }
    : { performedBy: 'dashboard' };
  const resp = await apiFetch(`/licenses/${id}/offline-activate`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return resp.json() as Promise<{ licenseFile: string | object }>;
}

export default function LicenseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [auditPage, setAuditPage] = useState(1);
  const [passportPage, setPassportPage] = useState(1);

  const licenseQuery = useQuery({
    queryKey: ['license', 'detail', id!],
    queryFn: () => fetchLicense(id!),
    staleTime: 30_000,
    enabled: !!id,
  });

  const devicesQuery = useQuery({
    queryKey: ['license', 'devices', id!],
    queryFn: () => fetchDevices(id!),
    enabled: !!id && !licenseQuery.isLoading && !licenseQuery.isError,
    staleTime: 30_000,
  });

  const activationRequestsQuery = useQuery({
    queryKey: ['license', 'activation-requests', id!],
    queryFn: () => fetchActivationRequests(id!),
    enabled: !!id && !licenseQuery.isLoading && !licenseQuery.isError,
    staleTime: 15_000,
  });

  const auditQuery = useQuery({
    queryKey: ['license', 'audit', id!, licenseQuery.data?.serial, auditPage],
    queryFn: async () => {
      try {
        const result = await fetchAudit(licenseQuery.data!.serial, auditPage);
        return { ...result, _source: 'audit' as const };
      } catch {
        const result = await fetchAuditFallback(id!);
        return { ...result, _source: 'fallback' as const };
      }
    },
    enabled: !!id && !!licenseQuery.data && !licenseQuery.isError,
    staleTime: 30_000,
  });

  const offlineProbe = useQuery({
    queryKey: ['license', 'offline-probe', id!],
    queryFn: async () => {
      try {
        const resp = await apiFetch(`/licenses/${id!}/offline-activate`, { method: 'HEAD' });
        return resp.ok;
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return false;
        return true;
      }
    },
    enabled: !!id && !licenseQuery.isLoading,
    staleTime: Infinity,
  });

  // Robot passport (#163) — the model / lifecycle state / audit history of the
  // machine. Fetched only for robot serials; a missing passport simply hides the
  // section rather than failing the page (the passport route is additive).
  const passportQuery = useQuery({
    queryKey: ['license', 'passport', licenseQuery.data?.serial, passportPage],
    queryFn: () => fetchPassport(licenseQuery.data!.serial, passportPage),
    enabled: !!licenseQuery.data && classifySerial(licenseQuery.data.serial) === 'robot',
    staleTime: 30_000,
    retry: false,
  });

  const revokeMutation = useMutation({
    mutationFn: (reason?: string) => revokeLicense(id!, reason),
    onMutate: () => {
      const prev = queryClient.getQueryData<LicenseDetail>(['license', 'detail', id!]);
      if (prev) {
        queryClient.setQueryData(['license', 'detail', id!], { ...prev, status: 'revoked' });
      }
      return { prev };
    },
    onSuccess: () => {
      toast.success(t('licenses.detail.toastRevoked', { serial: licenseQuery.data?.serial ?? id }));
      setRevokeOpen(false);
      queryClient.invalidateQueries({ queryKey: ['license', 'detail', id!] });
      queryClient.invalidateQueries({ queryKey: ['license', 'devices', id!] });
    },
    onError: (_err, _vars, context) => {
      if (context?.prev) {
        queryClient.setQueryData(['license', 'detail', id!], context.prev);
      }
      toast.error(t('licenses.detail.toastRevokeError'));
    },
  });

  const offlineMutation = useMutation({
    mutationFn: () =>
      offlineDownload(id!, devices?.data.find((d) => d.installationId)?.installationId ?? null),
    onSuccess: (data) => {
      // v2: licenseFile is the RRL2 envelope object → serialize as license.enc;
      // v1 (legacy): licenseFile is `payload.sig` string → save as .lic.
      const isV2 = typeof data.licenseFile !== 'string';
      const content = isV2 ? JSON.stringify(data.licenseFile) : (data.licenseFile as string);
      const blob = new Blob([content], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${licenseQuery.data?.serial ?? 'license'}.${isV2 ? 'enc' : 'lic'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
    onError: () => {
      toast.error(t('licenses.detail.toastOfflineError'));
    },
  });

  const approveMutation = useMutation({
    mutationFn: ({ requestId, seat }: { requestId: string; seat: string }) =>
      approveActivationRequest(requestId, seat),
    onSuccess: () => {
      toast.success(t('licenses.detail.activationRequests.toastApproved'));
      queryClient.invalidateQueries({ queryKey: ['license', 'activation-requests', id!] });
      queryClient.invalidateQueries({ queryKey: ['license', 'devices', id!] });
    },
    onError: () => {
      toast.error(t('licenses.detail.activationRequests.toastError'));
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ requestId, reason }: { requestId: string; reason: string }) =>
      rejectActivationRequest(requestId, reason),
    onSuccess: () => {
      toast.success(t('licenses.detail.activationRequests.toastRejected'));
      queryClient.invalidateQueries({ queryKey: ['license', 'activation-requests', id!] });
    },
    onError: () => {
      toast.error(t('licenses.detail.activationRequests.toastError'));
    },
  });

  if (!id) return <NotFoundState />;
  if (licenseQuery.isLoading) return <LoadingState />;

  const licenseError = licenseQuery.error as { status?: number } | undefined;
  if (licenseError?.status === 404) return <NotFoundState />;
  if (licenseQuery.error) return <ErrorState onRetry={() => licenseQuery.refetch()} />;

  const license = licenseQuery.data!;
  const devices = devicesQuery.data;
  const audit = auditQuery.data;
  const activationRequests = activationRequestsQuery.data?.data ?? [];
  // Live seats of this license — the transfer candidates. `GET /:id/devices` already
  // excludes factory baselines and returns released seats with a non-null
  // `deactivatedAt`, so a live seat is simply one without a deactivation stamp.
  const liveSeats: LiveSeat[] = (devices?.data ?? [])
    .filter((d) => d.deactivatedAt === null)
    .map((d) => ({
      id: d.id,
      installationId: d.installationId,
      fingerprintHash: d.fingerprintHash,
      activatedAt: d.activatedAt,
    }));
  const isDegraded = audit?._source === 'fallback';
  const showDownloadOffline = offlineProbe.data ?? false;
  const isRevoked = license.status === 'revoked';

  return (
    <div className="mx-auto w-full max-w-[1120px] space-y-10">
      <LicenseDetailHeader
        serial={license.serial}
        status={license.status}
        onDownloadOffline={() => offlineMutation.mutate()}
        showDownloadOffline={showDownloadOffline}
      />

      <LicenseMetadataGrid
        type={license.type}
        createdAt={license.createdAt}
        expiresAt={license.expiresAt}
        maxActivations={license.maxActivations}
        activationCount={license.activationCount}
      />

      {passportQuery.data && <RobotPassport passport={passportQuery.data} />}

      <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-[1fr_300px]">
        <div className="space-y-12">
          {devices && (
            <LicenseActivationsTable
              activations={devices.data}
              licenseId={id!}
              total={devices.data.length}
              page={1}
              pages={1}
              onPageChange={() => {}}
            />
          )}

          {activationRequests.length > 0 && (
            <ActivationRequestsSection
              requests={activationRequests}
              seats={liveSeats}
              onApprove={(requestId, seat) => approveMutation.mutate({ requestId, seat })}
              onReject={(requestId, reason) => rejectMutation.mutate({ requestId, reason })}
              busy={approveMutation.isPending || rejectMutation.isPending}
            />
          )}

          {passportQuery.data && (
            <PassportHistory
              entries={passportQuery.data.history}
              total={passportQuery.data.historyTotal}
              page={passportQuery.data.historyPage}
              pages={passportQuery.data.historyPages}
              onPageChange={setPassportPage}
            />
          )}

          {audit && (
            <LicenseActivityTimeline
              events={audit.data}
              total={audit.total}
              page={audit.page}
              pages={audit.pages}
              onPageChange={setAuditPage}
              degraded={isDegraded}
            />
          )}
        </div>

        <div className="space-y-10">
          <EntitlementsPanel entitlements={license.entitlements} />
          <RelatedLinks serial={license.serial} />
        </div>
      </div>

      {/* Danger zone — the single home of the destructive action */}
      {!isRevoked && (
        <section className="rounded-lg border border-destructive/40 bg-destructive/[0.04] px-5 py-6 sm:px-6">
          <h2 className="text-[15px] font-semibold text-destructive">
            {t('licenses.detail.dangerZone.title')}
          </h2>
          <div className="mt-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="max-w-[56ch] text-[13.5px] text-muted-foreground">
              {t('licenses.detail.dangerZone.revokeHint')}
            </p>
            <Button
              variant="destructive"
              className="shrink-0"
              onClick={() => setRevokeOpen(true)}
              disabled={revokeMutation.isPending}
              data-testid="revoke-btn"
            >
              {revokeMutation.isPending
                ? t('licenses.detail.revoking')
                : t('licenses.detail.revoke')}
            </Button>
          </div>
        </section>
      )}

      <ConfirmDestructiveDialog
        open={revokeOpen}
        onOpenChange={setRevokeOpen}
        title={t('licenses.detail.revokeDialog.title')}
        description={
          <Trans
            i18nKey="licenses.detail.revokeDialog.description"
            values={{ serial: license.serial }}
            components={{ strong: <strong /> }}
          />
        }
        confirmValue={license.serial}
        confirmLabel={t('licenses.detail.revoke')}
        onConfirm={(reason) => revokeMutation.mutate(reason)}
        isLoading={revokeMutation.isPending}
        reasonRequired={true}
      />
    </div>
  );
}

function EntitlementsPanel({ entitlements }: { entitlements: LicenseDetail['entitlements'] }) {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const hasExtras = entitlements.length > 4;
  const display = showAll ? entitlements : entitlements.slice(0, 4);
  const remaining = entitlements.length - 4;

  return (
    <section>
      <h2 className="mb-4 text-[15px] font-semibold tracking-tight text-foreground">
        {t('licenses.detail.entitlements')}
      </h2>
      <ul className="flex flex-wrap gap-2">
        {display.map((e, i) => (
          <li
            key={`${e.module}-${i}`}
            className="inline-flex items-baseline gap-1.5 rounded-md border border-border bg-secondary/50 px-2.5 py-1"
          >
            <span className="font-mono text-[12.5px] text-foreground">{e.module}</span>
            <span className="text-[11.5px] text-faint">{e.tier}</span>
          </li>
        ))}
      </ul>
      {hasExtras && !showAll && (
        <button
          className="mt-3 rounded-sm text-[13px] font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => setShowAll(true)}
        >
          {t('licenses.detail.more', { count: remaining })}
        </button>
      )}
    </section>
  );
}

function RelatedLinks({ serial }: { serial: string }) {
  const { t } = useTranslation();
  return (
    <section>
      <h2 className="mb-4 text-[15px] font-semibold tracking-tight text-foreground">
        {t('licenses.detail.related')}
      </h2>
      <Link
        to={`/audit?serial=${encodeURIComponent(serial)}`}
        className="group flex items-center justify-between gap-3 rounded-md border border-border px-3.5 py-2.5 transition-colors hover:bg-foreground/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="text-[13.5px] text-muted-foreground transition-colors group-hover:text-foreground">
          {t('licenses.detail.auditForSerial')}
        </span>
        <ChevronRight
          className="h-4 w-4 shrink-0 text-faint transition-colors group-hover:text-muted-foreground"
          aria-hidden="true"
        />
      </Link>
    </section>
  );
}

function LoadingState() {
  return (
    <div className="mx-auto w-full max-w-[1120px] space-y-10">
      <div className="space-y-3">
        <Skeleton className="h-5 w-24" />
        <div className="flex items-center gap-3">
          <Skeleton className="h-9 w-64" />
          <Skeleton className="h-6 w-20" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-x-8 gap-y-6 border-y py-7 sm:grid-cols-3 lg:grid-cols-5">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="space-y-2">
            <Skeleton className="h-3.5 w-16" />
            <Skeleton className="h-5 w-24" />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-[1fr_300px]">
        <div className="space-y-4">
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
        <div className="space-y-4">
          <Skeleton className="h-6 w-28" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    </div>
  );
}

function NotFoundState() {
  const { t } = useTranslation();
  return (
    <div className="flex h-[60vh] flex-col items-center justify-center gap-4">
      <h2 className="font-serif text-2xl italic tracking-tight text-foreground">
        {t('licenses.detail.notFound')}
      </h2>
      <Button asChild variant="outline">
        <Link to="/licenses">{t('licenses.detail.backToLicenses')}</Link>
      </Button>
    </div>
  );
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="mx-auto w-full max-w-[1120px]">
      <div className="flex items-center justify-between rounded-lg border border-destructive/50 bg-destructive/10 p-4">
        <p className="text-sm text-destructive">{t('licenses.detail.loadError')}</p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          {t('common.retry')}
        </Button>
      </div>
    </div>
  );
}

function seatSignature(seat: LiveSeat): string {
  const name =
    seat.installationId && seat.installationId.length > 0
      ? seat.installationId
      : seat.fingerprintHash.slice(0, 8);
  return `${name} · ${new Date(seat.activatedAt).toLocaleDateString()}`;
}

function ActivationRequestCard({
  request,
  seats,
  onApprove,
  onReject,
  busy,
}: {
  request: ActivationRequest;
  seats: LiveSeat[];
  onApprove: (requestId: string, seat: string) => void;
  onReject: (requestId: string) => void;
  busy: boolean;
}) {
  const { t } = useTranslation();
  const sorted = [...seats].sort((a, b) => a.activatedAt.localeCompare(b.activatedAt));
  // Operator override; defaults to the oldest live seat and falls back to it when
  // the chosen seat disappears from a refreshed seat list.
  const [seatOverride, setSeatOverride] = useState<string | null>(null);
  const selected =
    seatOverride && sorted.some((s) => s.id === seatOverride)
      ? seatOverride
      : (sorted[0]?.id ?? '');

  return (
    <li
      data-testid={`activation-request-${request.id}`}
      className="flex flex-col gap-3 rounded-lg border border-border px-4 py-3"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="truncate font-mono text-[13px] text-foreground">{request.installationId}</p>
          <p className="mt-0.5 text-[12px] text-faint">
            {request.clientVersion
              ? t('licenses.detail.activationRequests.clientVersion', {
                  version: request.clientVersion,
                })
              : new Date(request.createdAt).toLocaleString()}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={busy || !selected}
            onClick={() => selected && onApprove(request.id, selected)}
            data-testid={`approve-activation-request-${request.id}`}
          >
            {t('licenses.detail.activationRequests.approve')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="text-destructive hover:text-destructive"
            disabled={busy}
            onClick={() => onReject(request.id)}
            data-testid={`reject-activation-request-${request.id}`}
          >
            {t('licenses.detail.activationRequests.reject')}
          </Button>
        </div>
      </div>

      {request.status === 'transfer_pending' && (
        <p
          className="text-[12.5px] text-muted-foreground"
          data-testid={`activation-request-note-${request.id}`}
        >
          {t('licenses.detail.activationRequests.transferPendingNotice')}
        </p>
      )}

      <div className="flex flex-col gap-1.5">
        <Label
          htmlFor={`activation-seat-${request.id}`}
          className="text-[12px] text-muted-foreground"
        >
          {t('licenses.detail.activationRequests.seatLabel')}
        </Label>
        {sorted.length === 0 ? (
          <p className="text-[12.5px] text-faint" data-testid={`no-seats-${request.id}`}>
            {t('licenses.detail.activationRequests.noSeats')}
          </p>
        ) : (
          <Select value={selected} onValueChange={setSeatOverride}>
            <SelectTrigger
              id={`activation-seat-${request.id}`}
              className="h-9 w-full sm:w-[320px]"
              data-testid={`seat-picker-${request.id}`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {sorted.map((seat) => (
                <SelectItem key={seat.id} value={seat.id} data-testid={`seat-option-${seat.id}`}>
                  {seatSignature(seat)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
    </li>
  );
}

function ActivationRequestsSection({
  requests,
  seats,
  onApprove,
  onReject,
  busy,
}: {
  requests: ActivationRequest[];
  seats: LiveSeat[];
  onApprove: (requestId: string, seat: string) => void;
  onReject: (requestId: string, reason: string) => void;
  busy: boolean;
}) {
  const { t } = useTranslation();
  const [rejectTarget, setRejectTarget] = useState<ActivationRequest | null>(null);
  const pending = requests.filter((r) => r.status === 'transfer_pending' || r.status === 'pending');

  return (
    <section>
      <div className="mb-4 flex items-center gap-2">
        <Inbox className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        <h2 className="text-[15px] font-semibold tracking-tight text-foreground">
          {t('licenses.detail.activationRequests.title')}
        </h2>
      </div>
      {pending.length === 0 ? (
        <p className="text-[13.5px] text-muted-foreground">
          {t('licenses.detail.activationRequests.empty')}
        </p>
      ) : (
        <ul className="space-y-3">
          {pending.map((r) => (
            <ActivationRequestCard
              key={r.id}
              request={r}
              seats={seats}
              onApprove={onApprove}
              onReject={(requestId) =>
                setRejectTarget(requests.find((x) => x.id === requestId) ?? null)
              }
              busy={busy}
            />
          ))}
        </ul>
      )}

      <RejectActivationDialog
        request={rejectTarget}
        onOpenChange={(open) => !open && setRejectTarget(null)}
        onConfirm={(reason) => {
          if (!rejectTarget) return;
          onReject(rejectTarget.id, reason);
          setRejectTarget(null);
        }}
        busy={busy}
      />
    </section>
  );
}

function RejectActivationDialog({
  request,
  onOpenChange,
  onConfirm,
  busy,
}: {
  request: ActivationRequest | null;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: string) => void;
  busy: boolean;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');

  // Reset the reason each time a new target opens.
  useEffect(() => {
    setReason('');
  }, [request?.id]);

  return (
    <Dialog open={request !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('licenses.detail.activationRequests.rejectDialogTitle')}</DialogTitle>
          <DialogDescription asChild>
            <div>
              <Trans
                i18nKey="licenses.detail.activationRequests.rejectDialogDescription"
                values={{ installationId: request?.installationId ?? '' }}
                components={{ strong: <strong /> }}
              />
            </div>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="activation-reject-reason">
            {t('licenses.detail.activationRequests.rejectReasonLabel')}
          </Label>
          <textarea
            id="activation-reject-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
            rows={3}
            className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            data-testid="activation-reject-reason"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={() => reason.trim() && onConfirm(reason.trim())}
            disabled={busy || reason.trim().length === 0}
            data-testid="activation-reject-confirm"
          >
            {t('licenses.detail.activationRequests.reject')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const handle = { crumb: 'Details' };
